import { useState } from 'react';
import PhoneInput from 'react-phone-number-input';
import Link from 'next/link';
import { useRouter } from 'next/router';
import ImageQuestion from '@/assets/img/image-question.svg';
import { CustomCheckbox } from '@/components/Formik/CustomCheckbox';
import { CustomForm } from '@/components/Formik/CustomForm';
import { CustomTextField } from '@/components/Formik/CustomTextField';
import InformationCard from '@/components/InformationCard';
import ModalDefault from '@/components/Modals/ModalDefault';
import NewPhoneInput from '@/components/PhoneInput';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { ROUTER_PAGES } from '@/utils/constants';
import { handleErrorAsync } from '@/utils/handlers';
import { removeFalsyValues } from '@/utils/objectUtils';
import { registerFormSchema } from '@/validators/cadastro';
import { InfoOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { Field, FieldProps, FormikHelpers } from 'formik';

interface IFormValues {
  email: string;
  phone: string;
  name: string;
  cpf: string;
  password: string;
  re_password: string;
  agreePrivacyPolicy: boolean;
}

interface IRegisterFormProps {
  onSubmit: (formValues: IFormValues) => Promise<void>;
  initialValues: Nullables<
    Partial<{
      email: string;
      phone: string;
      name: string;
      cpf: string;
    }>
  >;
}

const RegisterForm = ({ onSubmit, initialValues }: IRegisterFormProps) => {
  const { email, phone, name, cpf } = initialValues;
  const { query } = useRouter();

  const [showPassword, setShowPassword] = useState(false);
  const [showRePassword, setShowRePassword] = useState(false);
  const [isOpenModalQuestionCPF, setIsOpenModalQuestionCPF] = useState(false);

  const handleCloseModalQuestionCPF = () => {
    setIsOpenModalQuestionCPF(false);
  };

  const toggleShowPassword = () => setShowPassword(prevState => !prevState);
  const toggleShowRePassword = () => setShowRePassword(prevState => !prevState);

  const submitFormUserData = async (
    values: IFormValues,
    formikHelpers: FormikHelpers<IFormValues>
  ) => {
    const [_, error] = await handleErrorAsync(async () => {
      await checkUserData({
        email: values.email,
        phone: values.phone,
        cpf: values.cpf,
        password: values.password,
        name: values.name,
      });

      return await onSubmit(values);
    });

    if (error) {
      if (error.message === 'too much recursion') {
        Toast('error', 'Muitos caracteres iguais!');
      } else {
        Toast('error', error.message);
      }

      return;
    }

    formikHelpers.resetForm();
  };

  const checkUserData = async (payload: {
    email: string;
    phone: string;
    cpf: string;
    password: string;
    name: string;
  }) => {
    await api.post('/auth/visitors/check/', { ...removeFalsyValues(payload) });
  };

  return (
    <>
      <CustomForm
        initialValues={{
          email: query.email ? query.email.toString() : email || '',
          phone: query.telefone ? `+55${query.telefone}` : phone || '',
          name: query.nome ? query.nome.toString() : name || '',
          cpf: cpf || '',
          password: '',
          re_password: '',
          agreePrivacyPolicy: false,
        }}
        validationSchema={registerFormSchema}
        onSubmit={submitFormUserData}
        buttonText="cadastrar"
        sx={{
          gap: 2,
        }}
      >
        <Divider
          sx={theme => ({
            color: '#979797',
            borderWidth: '1px',
            borderRadius: '2px',
            mb: 3,
            [theme.breakpoints.up('sm')]: {
              display: 'none',
            },
          })}
        />

        <Typography
          sx={{
            color: 'text.secondary',
            fontWeight: 'bold',
          }}
          variant="h5"
        >
          Crie seu usuário
        </Typography>
        <Typography
          sx={{ color: '#000000', opacity: '50%', mb: 0.5 }}
          variant="body1"
        >
          Confirme seus dados pessoais para criar uma conta no Obra Play.
        </Typography>
        <CustomTextField
          name="name"
          label="Qual seu nome completo?*"
          placeholder="Qual seu nome completo?*"
        />
        <Field name="phone">
          {({ field, form }: FieldProps) => (
            <PhoneInput
              {...field}
              country="BR"
              defaultCountry="BR"
              placeholder="(00) 00000-0000"
              initialValueFormat="national"
              label="Qual o número do seu celular?"
              limitMaxLength
              inputComponent={NewPhoneInput}
              onChange={newValue =>
                form.setValues({ ...form.values, phone: newValue })
              }
              error={
                form.touched[field.name] && Boolean(form.errors[field.name])
              }
              helperText={form.touched[field.name] && form.errors[field.name]}
            />
          )}
        </Field>
        <CustomTextField
          name="cpf"
          label="Qual o seu CPF?*"
          placeholder="Qual o seu CPF?*"
        />
        <CustomTextField
          name="email"
          label="Qual o seu e-mail? (Que só você tenha acesso)*"
          placeholder="Qual o seu e-mail? (Que só você tenha acesso)*"
        />
        <CustomTextField
          name="password"
          type={showPassword ? 'text' : 'password'}
          label="Defina uma senha de acesso"
          placeholder="Defina uma senha de acesso"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={toggleShowPassword}
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <CustomTextField
          name="re_password"
          type={showRePassword ? 'text' : 'password'}
          label="Repita a senha"
          placeholder="Repita a senha"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={toggleShowRePassword}
                >
                  {showRePassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <InformationCard text="Utilize letras maiúsculas, letras minúsculas, números, símbolos e no mínimo 8 caracteres." />

        <Box component="section" marginTop={2} marginBottom={1}>
          <Button
            startIcon={<InfoOutlined />}
            onClick={() => setIsOpenModalQuestionCPF(true)}
            sx={{
              textTransform: 'none',
              textDecoration: 'underline',
              pl: 0.5,
            }}
          >
            Por que preciso informar o meu CPF?
          </Button>
          <CustomCheckbox
            name="agreePrivacyPolicy"
            label={
              <Box
                sx={theme => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'default',
                  flexWrap: 'wrap',
                  mt: 0,
                  [theme.breakpoints.down(470)]: {
                    mt: 3,
                  },
                })}
              >
                <Typography variant="body2">Li e concordo com os</Typography>
                <Link href={ROUTER_PAGES.termosUso} passHref>
                  <a target="_blank" rel="noreferrer">
                    <Typography
                      variant="body2"
                      sx={{ cursor: 'pointer' }}
                      color="primary"
                    >
                      Termos de uso
                    </Typography>
                  </a>
                </Link>
                <Typography variant="body2">e</Typography>
                <Link href={ROUTER_PAGES.privacidade} passHref>
                  <a target="_blank" rel="noreferrer">
                    <Typography
                      variant="body2"
                      sx={{ cursor: 'pointer' }}
                      color="primary"
                    >
                      Política de Privacidade
                    </Typography>
                  </a>
                </Link>
              </Box>
            }
          />
        </Box>
      </CustomForm>

      <ModalDefault
        isOpen={isOpenModalQuestionCPF}
        onClose={() => handleCloseModalQuestionCPF()}
        src={ImageQuestion}
        alt="Imagem por que precisa informar CPF"
      >
        {
          <Box sx={{ textAlign: 'left' }}>
            <Typography color="#808080" variant="subtitle1">
              Por que preciso informar o meu CPF?
            </Typography>
            <Typography
              color="#808080"
              variant="body2"
              sx={{ fontWeight: '300' }}
            >
              Para acessar novas cotações no Obra Play precisamos que seu
              cadastro possua um identificador único, que será o seu CPF.
            </Typography>
          </Box>
        }
      </ModalDefault>
    </>
  );
};

export type { IRegisterFormProps };
export default RegisterForm;
